// ImmersiveNewsArticle.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SafeAreaView, StyleSheet, View, TouchableOpacity, Platform, Pressable } from 'react-native';
import { RouteProp, useFocusEffect, useNavigation, useRoute } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import { useThemeSettings } from '../../ThemeProvider';
import type { RootStackParamList } from '../../shared/types/RootStackParamList';
import { ROUTES } from '../../shared/constants/routes';
import { useFullArticleDetails } from './data/useFullArticleDetails';
import CustomLinearGradient, { GRADIENT_TYPE } from '../../components/CustomLinearGradient';
import { usePlatform } from '../../providers/PlatformProvider';
import StackedCarousel from './components/StackedCarousel';
import { createImmersiveArticleCards, CardData } from './helpers/renderContent';
import { CustomIcon } from '../../components/CustomIcon';
import getGlobalStyles from '../../globalStyles';
import { useModalStore, ModalType } from '../../store/modalStore';
import { useActiveArticle } from '../news-feed/hooks/useActiveArticle';
import VerticalGestureHandler from '../../components/VerticalGestureHandler';
import { Text } from 'react-native-paper';
import { FULL_HEIGHT } from '../../shared/constants/dimension';
import AppLoader from '../../components/AppLoader';
import { useCollectionStore } from '../../store/collectionStore';
import { useTrackScreenTime } from '../../analytics/useTrackScreenTime';
import { useTourStore } from '../../store/useTourStore';
import { usePrefetchAdjacentArticles } from '../../hooks/usePrefetchAdjacentArticles';

type RouteProps = RouteProp<RootStackParamList, typeof ROUTES.IMMERSRSIVE_NEWS_ARTICLE>;

export const ImmersiveNewsArticle = () => {
  useTrackScreenTime(ROUTES.IMMERSRSIVE_NEWS_ARTICLE);

  const [cards, setCards] = useState<CardData[]>([]);
  const { theme } = useThemeSettings();
  const gStyles = useMemo(() => getGlobalStyles(theme), [theme]);
  const { t } = useTranslation();
  const route = useRoute<RouteProps>();
  const params = route.params as any;
  const articleId = params?.articleId ?? '';
  const openedFromSwipe = params?.fromSwipe === true;
  const { showWebVersion } = usePlatform();
  const { setModalShown } = useModalStore();
  const { selectedCollection } = useCollectionStore();
  const { data: fullArticleDetails, isFetching: isFullArticleDetailsFetching } = useFullArticleDetails(
    articleId
  );

  const navigation = useNavigation();
  const { nextArticle, previousArticle, setActiveArticleId } = useActiveArticle();

  const { incrementSwipeHint, shouldShowImmersiveSwipeHint } = useTourStore();
  const [isSwipeHintVisible, setIsSwipeHintVisible] = useState(false);

  const [isVerticalGestureDisabled, setVerticalGestureDisabled] = useState(false);
  const hasCountedThisFocusRef = useRef(false);


  usePrefetchAdjacentArticles(nextArticle?.id, previousArticle?.id);

  // Set active article ID on mount
  useEffect(() => {
    setCards([]);
    if (articleId) setActiveArticleId(articleId);
  }, [articleId, setActiveArticleId]);

  useFocusEffect(
    useCallback(() => {
      if (Platform.OS === 'web') return () => { };

      if (!hasCountedThisFocusRef.current && !openedFromSwipe) {
        const canShow = shouldShowImmersiveSwipeHint();
        setIsSwipeHintVisible(canShow);
        incrementSwipeHint(); // counts page opens (persisted)
        hasCountedThisFocusRef.current = true;
      }

      return () => {
        hasCountedThisFocusRef.current = false;
        setIsSwipeHintVisible(false);
      };
    }, [incrementSwipeHint, shouldShowImmersiveSwipeHint])
  );

  const dismissSwipeHints = () => setIsSwipeHintVisible(false);

  const swipeNext = () => {
    if (nextArticle?.id) {
      (navigation as any).navigate(ROUTES.IMMERSRSIVE_NEWS_ARTICLE, {
        articleId: nextArticle?.id,
        collectionId: selectedCollection?.id,
        fromSwipe: true,
      });
    }
  };

  const swipePrevious = () => {
    if (previousArticle?.id) {
      (navigation as any).navigate(ROUTES.IMMERSRSIVE_NEWS_ARTICLE, {
        articleId: previousArticle?.id,
        collectionId: selectedCollection?.id,
        fromSwipe: true,
      });
    }
  };

  const handleSwipeNext = () => {
    if (isSwipeHintVisible) dismissSwipeHints();
    swipeNext();
  };

  const handleSwipePrevious = () => {
    if (isSwipeHintVisible) dismissSwipeHints();
    swipePrevious();
  };

  // Generate carousel cards when full article data is ready
  useEffect(() => {
    if (!isFullArticleDetailsFetching && fullArticleDetails) {
      const detailCards = createImmersiveArticleCards(fullArticleDetails, theme, t, () =>
        setModalShown(ModalType.SHARE_ARTICLE, {
          article: fullArticleDetails,
          collection: selectedCollection,
          sharedFrom: ROUTES.IMMERSRSIVE_NEWS_ARTICLE,
        })
      );
      setCards(detailCards);
    }
  }, [isFullArticleDetailsFetching, fullArticleDetails, theme]);
  const getFontSize = (title: string) => {
    if (!title) return 16;
    if (title.length > 80) return 12;
    if (title.length > 50) return 14;
    return 16;
  };

  // Memoized titleComponent to avoid flicker
  const titleComponent = useMemo(() => {
    return (
      <View style={styles.titleContainer}>
        <Text style={{ flex: 1, fontSize: getFontSize(fullArticleDetails?.title)}}>{fullArticleDetails?.title}</Text>
        <TouchableOpacity
          style={[gStyles.buttonStyle, gStyles.buttonCircle]}
          onPress={() =>
            setModalShown(ModalType.SHARE_ARTICLE, {
              article: fullArticleDetails,
              collection: selectedCollection,
              sharedFrom: ROUTES.IMMERSRSIVE_NEWS_ARTICLE,
            })
          }
        >
          <CustomIcon icon="share" size={16} color={theme?.colors?.primary} />
        </TouchableOpacity>
      </View>
    );
  }, [fullArticleDetails?.title, gStyles, setModalShown, selectedCollection, theme?.colors?.primary]);

  // Swipe buttons for web / native reference
  const getSwipeButton = () => {
    if (isFullArticleDetailsFetching || cards.length === 0) return null;

    if (Platform.OS === 'web') {
      return (
        <View style={[styles.swipeControls, gStyles.fullWidth]}>
          <TouchableOpacity style={gStyles.centerRow} disabled={!previousArticle} onPress={swipePrevious}>
            {previousArticle ? <><Text style={{ color: theme.colors.textPrimary }}>
              {t(`screens.article.${Platform.OS === 'web' ? 'previous_article' : 'swipe_up'}`)}
            </Text>
              <CustomIcon icon="upSwipeWeb" size={18} color={theme.colors.textPrimary} />
            </> : <></>}
          </TouchableOpacity>

          {nextArticle && <TouchableOpacity style={[gStyles.centerRow, { alignSelf: "flex-end" }]} disabled={!nextArticle} onPress={swipeNext}>
            <Text style={{ color: theme.colors.textPrimary }}>
              {t(`screens.article.${Platform.OS === 'web' ? 'next_article' : 'swipe_down'}`)}
            </Text>
            <CustomIcon icon="downSwipeWeb" size={18} color={theme.colors.textPrimary} />
          </TouchableOpacity>}
        </View>
      );
    }

    if (!isSwipeHintVisible) return null;
    return (
      <Pressable style={styles.swipeOverlay} onPress={dismissSwipeHints}>
        <View style={styles.swipeBackdrop} pointerEvents="none" />
        <View style={styles.swipeHintsRow}>
          <View style={styles.swipeHintLeft}>
            <TouchableOpacity
              style={[styles.swipeControl, gStyles.centerRow]}
              disabled={!nextArticle}
              onPress={() => {
                dismissSwipeHints();
                swipeNext();
              }}
            >
              <CustomIcon icon="swipe_down" size={25} color={theme.colors.textPrimary} />
              <Text style={{ color: theme.colors.black }}>{t('screens.article.swipe_down')}</Text>
            </TouchableOpacity>
          </View>
          <View style={styles.swipeHintRight}>
            <TouchableOpacity
              style={[styles.swipeControl, gStyles.centerRow]}
              disabled={!previousArticle}
              onPress={() => {
                dismissSwipeHints();
                swipePrevious();
              }}
            >
              <CustomIcon icon="swipe_up" size={25} color={theme.colors.textPrimary} />
              <Text style={{ color: theme.colors.black }}>{t('screens.article.swipe_up')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Pressable>
    );
  };

  return (
    <SafeAreaView
      style={[
        gStyles.centerContainer,
        !isFullArticleDetailsFetching && gStyles.centerColumn,
        Platform.OS !== "web" && { marginBottom: 24 },
        !showWebVersion && { top: -10, gap: 8 },
      ]}
    >
      <VerticalGestureHandler
        swipeDown={handleSwipePrevious}
        swipeUp={handleSwipeNext}
        disabled={isVerticalGestureDisabled || Platform.OS === 'web' || isFullArticleDetailsFetching}
      >
        {isFullArticleDetailsFetching ? (
          <AppLoader isLoading />
        ) : showWebVersion ? (
          <CustomLinearGradient
            gradientType={GRADIENT_TYPE.APP_CARD}
            style={[gStyles.webCard, gStyles.centerColumn, { height: FULL_HEIGHT, gap: 0 }]}
          >
            <StackedCarousel titleComponent={titleComponent} cards={cards} setVerticalGestureDisabled={setVerticalGestureDisabled} />
            {getSwipeButton()}
          </CustomLinearGradient>
        ) : (
          <View style={{ flex: 1 }}>
            <StackedCarousel titleComponent={titleComponent} cards={cards} setVerticalGestureDisabled={setVerticalGestureDisabled} />
          </View>
        )}
      </VerticalGestureHandler>
      {!showWebVersion && getSwipeButton()}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  titleContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: 16,
    justifyContent: 'space-around',
    alignItems: 'center',
  },
  swipeControls: {
    alignSelf: 'center',
    minWidth: 100,
    flexDirection: 'row',
    gap: 16,
    paddingHorizontal: 16,
    justifyContent: 'space-between',
  },
  swipeOverlay: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'flex-end',
  },
  swipeBackdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.35)',
  },
  swipeHintsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingBottom: 20,
    width: '100%',
  },
  swipeHintLeft: {
    alignSelf: 'flex-end',
  },
  swipeHintRight: {
    alignSelf: 'flex-end',
  },
  swipeControl: {
    alignSelf: 'center',
    backgroundColor: 'white',
    borderRadius: 16,
    paddingHorizontal: 20,
    paddingVertical: 12,
    flexDirection: 'row',
    gap: 12,
    shadowColor: '#000',
    shadowOpacity: 0.1,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 4 },
    elevation: 4,
  }
});
